// src/state/messagingStore.js
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export const useMessagingStore = create(
  persist(
    (set, get) => ({

      // UI state
      isOpen: false,

      // Which thread is selected in the right panel (room id/alias or app thread id)
      selectedThread: null,

      // Optional “starter” payload when launched from RecruitingModal (ensures candidate DM)
      candidateStarter: null,

  // Actions
  open: () => set({ isOpen: true, selectedThread: null }),
  close: () => set({ isOpen: false, candidateStarter: null }),
  forceUpdate: () => set((state) => ({ ...state })),

  // Local-only invite thread (no Matrix, no backend)
  createLocalInviteThread: (payload) => {
    if (!payload || typeof payload !== 'object') return;

    const myUserId = Number(window.localStorage.getItem('u_id'));
    const candidateUserId = Number(payload.candidateId);

    if (!Number.isFinite(myUserId) || !Number.isFinite(candidateUserId)) {
      console.error('[messagingStore] Invalid user IDs for local invite thread', {
        myUserId,
        candidateUserId,
      });
      return;
    }

    // Canonical alias for this pair (same pattern as Matrix DM)
    const a = Math.min(myUserId, candidateUserId);
    const b = Math.max(myUserId, candidateUserId);
    const aliasFull = `#eh.dm.u${a}_u${b}:ehandshake.com`;

    try {
      const state = get();
      const optimisticThreads = state.optimisticThreads || [];
      const archivedThreads = state.archivedThreads || [];
      const completedMap = state.completedInvites || {};

      // If this peer's invite has already completed (accepted + DM exists),
      // do NOT recreate the "Invite sent successfully" UI. Just open ChatCenter.
      if (completedMap[candidateUserId]) {
        console.log(
          '[messagingStore] createLocalInviteThread: invite already completed for peer, skipping optimistic invite UI',
          { candidateUserId }
        );
        set({ isOpen: true });
        return;
      }

      // 1) If there is already an active optimistic thread for this alias,
      //    just focus it in ChatCenter (no new card).
      const existingActive = optimisticThreads.find(
        (t) => String(t.alias) === String(aliasFull)
      );
      if (existingActive) {
        set({ selectedThread: aliasFull });
        return;
      }

      // 2) If there is an archived thread for this alias, unarchive it and focus it.
      const existingArchived = archivedThreads.find(
        (t) => String(t.alias) === String(aliasFull)
      );
      if (existingArchived) {
        if (typeof state.unarchiveThread === 'function') {
          state.unarchiveThread(aliasFull);
        }
        set({ selectedThread: aliasFull });
        return;
      }

      // 3) Otherwise, create a brand-new optimistic invite thread and focus it.
      state.addOptimisticDM({
        alias: aliasFull,
        peerUid: candidateUserId,
        displayName: payload.candidateName || null,
        avatarUrl: payload.candidateAvatar || null,
        lastMessagePreview: 'Chat Request Pending',
        lastMessageAt: Date.now(),
        direction: 'outgoing',
        inviteSent: false,
      });

      set({ selectedThread: aliasFull });
    } catch (e) {
      console.error('[messagingStore] Failed to create local invite thread', e);
    }
  },



  setSelectedThread: (threadRef) => set({ selectedThread: threadRef || null }),
  clearCandidateStarter: () => set({ candidateStarter: null }),

  // --- Completed invites (inviter side) ---
  // Once a peer's invite has been accepted / DM created, we mark that peer
  // so we no longer recreate the "Invite sent successfully" UI for that pair.
  // shape: { [peerUid:number]: true }
  completedInvites: {},

  // --- NEW: Pending Threads (pre-room conversations) ---
  // shape: { [peerUid:number]: { id:string, peerUid:number, createdAt:number, name?:string|null, avatarUrl?:string|null } }
  pendingThreads: {},


  // Replace all pending threads (rare)
  setAllPendingThreads(obj) {
    const safe = obj && typeof obj === 'object' ? obj : {};
    set(() => ({ pendingThreads: safe }));
    try { localStorage.setItem('eh_pending_threads', JSON.stringify(safe)); } catch {}
  },

  // Add/update one pending thread and persist
  addPendingThread(peerUid, data) {
    const key = Number(peerUid);
    if (!Number.isFinite(key)) return;
    set((s) => {
      const next = { ...(s.pendingThreads || {}) };
      next[key] = {
        id: data?.id || `pending:u${key}`,
        peerUid: key,
        createdAt: Number(data?.createdAt) || Date.now(),
        name: data?.name || null,
        avatarUrl: data?.avatarUrl || null,
      };
      try { localStorage.setItem('eh_pending_threads', JSON.stringify(next)); } catch {}
      return { pendingThreads: next };
    });
  },

  // Remove one pending thread and persist
  removePendingThread(peerUid) {
    const key = Number(peerUid);
    if (!Number.isFinite(key)) return;
    set((s) => {
      const next = { ...(s.pendingThreads || {}) };
      delete next[key];
      try { localStorage.setItem('eh_pending_threads', JSON.stringify(next)); } catch {}
      return { pendingThreads: next };
    });
  },

  // --- Optimistic DM threads (render instantly by canonical alias) ---
  // shape element: {
  //   alias: string,             // "#eh.dm.u<min>_u<max>:domain"
  //   peerUid: number,
  //   displayName: string|null,
  //   avatarUrl: string|null,
  //   lastMessagePreview: string|null,
  //   lastMessageAt: number,     // Date.now()
  //   isOptimistic: true
  // }
  optimisticThreads: [],

  // --- Archived DM threads (declined, cancelled, manually archived, etc.) ---
  // Same shape as optimisticThreads, but not shown in the main list.
  archivedThreads: [],

  addOptimisticDM(entry) {
    try {
      const alias = String(entry?.alias || '').trim();
      const peerUid = Number(entry?.peerUid);
      if (!alias || !/^#eh\.dm\.u\d+_u\d+:/i.test(alias) || !Number.isFinite(peerUid)) return;

      set((s) => {
        const existingOptimistic = s.optimisticThreads || [];
        const existingArchived   = s.archivedThreads || [];

        // Remove any previous instances of this alias from BOTH lists.
        const filteredOptimistic = existingOptimistic.filter(
          (t) => String(t.alias) !== alias
        );
        const filteredArchived = existingArchived.filter(
          (t) => String(t.alias) !== alias
        );

        const row = {
          // core identity
          alias,
          peerUid,

          // display data (inviter / peer)
          displayName: entry?.displayName ?? null,
          avatarUrl: entry?.avatarUrl ?? null,
          lastMessagePreview: entry?.lastMessagePreview ?? 'Start a conversation…',
          lastMessageAt: Number(entry?.lastMessageAt) || Date.now(),

          // invitation metadata (used by ChatView)
          direction: entry?.direction || null,          // 'incoming' | 'outgoing' | null
          inviteSent: !!entry?.inviteSent,              // true when already written to DB
          dmId: entry?.dmId ?? null,                    // dm_relationships.dm_id, if known
          status: entry?.status || 'pending',           // 'pending' | 'accepted' | 'declined' | 'blocked'

          // marker that this is a pre-Matrix optimistic record
          isOptimistic: true,
          createdAt: Date.now(),
        };

        console.log('[messagingStore] addOptimisticDM created row:', row);

        // New optimistic list: fresh row at top, everything else below.
        const nextOptimistic = [row, ...filteredOptimistic];

        // Single source of truth: no copies of this alias in archive.
        return {
          optimisticThreads: nextOptimistic,
          archivedThreads: filteredArchived,
        };
      });
    } catch (e) {
      console.error('[messagingStore] addOptimisticDM error:', e);
    }
  },

  // Generic immutable patch helper for a single optimistic thread
  updateOptimisticThread(alias, patch) {
    const a = String(alias || '').trim();
    if (!a || !patch || typeof patch !== 'object') return;

    set((s) => {
      const list = s.optimisticThreads || [];
      const idx = list.findIndex(
        (t) => t && String(t.alias || '') === a
      );
      if (idx === -1) {
        return {};
      }

      const next = [...list];
      next[idx] = {
        ...next[idx],
        ...patch,
      };

      return { optimisticThreads: next };
    });
  },

  reconcileDM({ alias, roomId }) {
    const a = String(alias || '').trim();
    if (!a) return;

    set((s) => {
      const rest = (s.optimisticThreads || []).filter(t => String(t.alias) !== a);
      // Zustand + IndexedDB persist will handle saving this
      return { optimisticThreads: rest };
    });
  },


  removeOptimisticDM(alias) {
    const a = String(alias || '').trim();
    if (!a) return;

    set((s) => {
      const rest = (s.optimisticThreads || []).filter(t => String(t.alias) !== a);
      // Zustand + IndexedDB persist will handle saving this
      return { optimisticThreads: rest };
    });
  },

  // Move a thread from optimisticThreads into archivedThreads (used on decline, manual archive, etc.)
  archiveThread(alias) {
    const a = String(alias || '').trim();
    if (!a) return;

    set((s) => {
      const list = s.optimisticThreads || [];
      const idx = list.findIndex(t => String(t.alias) === a);
      if (idx === -1) {
        return {};
      }

      // Mark status as declined by default when archiving
      const thread = { ...list[idx], status: 'declined' };

      const remaining = list.filter((_, i) => i !== idx);
      const archived = s.archivedThreads || [];
      const nextArchived = [thread, ...archived];

      return {
        optimisticThreads: remaining,
        archivedThreads: nextArchived,
      };
    });
  },

  // Move a thread back from archivedThreads to optimisticThreads (for future “unarchive” UX)
  unarchiveThread(alias) {
    const a = String(alias || '').trim();
    if (!a) return;

    set((s) => {
      const archived = s.archivedThreads || [];
      const idx = archived.findIndex(t => String(t.alias) === a);
      if (idx === -1) {
        return {};
      }

      const thread = archived[idx];
      const remainingArchived = archived.filter((_, i) => i !== idx);
      const optimistic = s.optimisticThreads || [];
      const nextOptimistic = [thread, ...optimistic];

      return {
        archivedThreads: remainingArchived,
        optimisticThreads: nextOptimistic,
      };
    });
  },

  // Persistently mark an optimistic invite thread as "sent"
  markInviteSent(alias) {
    const a = String(alias || '').trim();
    if (!a) return;

    set((s) => {
      const list = s.optimisticThreads || [];
      const next = list.map((t) =>
        String(t.alias) === a
          ? { ...t, inviteSent: true }
          : t
      );
      return { optimisticThreads: next };
    });
  },


  // Ingest sent invites (from /api/dm/sent) as outgoing invite threads for the inviter
  ingestSentInvites(invites) {
    try {
      const arr = Array.isArray(invites) ? invites : [];
      if (!arr.length) return;

      set((s) => {
        const existing = s.optimisticThreads || [];
        const updated = [...existing];
        const additions = [];

        const archived = s.archivedThreads || [];
        const archivedAliasSet = new Set(
          archived.map((t) => String(t.alias || ''))
        );

        // Copy existing completedInvites so we can update it from backend truth
        const completedInvites = { ...(s.completedInvites || {}) };

        arr.forEach((row) => {
          if (!row) return;

          const dmId = row.dm_id;
          const inviterId = row.u_id_inviter;
          const inviteeId = row.u_id_invitee;

          if (!inviterId || !inviteeId) return;

          // canonical DM alias for this pair
          const a = Math.min(inviterId, inviteeId);
          const b = Math.max(inviterId, inviteeId);
          const alias = `#eh.dm.u${a}_u${b}:ehandshake.com`;
          if (!alias) return;

          const status = row.status || 'pending';

          // If this invite has been accepted or already linked to a DM room,
          // record that the invite lifecycle is completed for this peer
          // and do NOT recreate an optimistic "pending" thread for it.
          if (status === 'accepted') {
            const key = Number(inviteeId);
            if (Number.isFinite(key)) {
              completedInvites[key] = true;
            }
            return;
          }

          // If this DM is already archived locally and still declined,
          // do not recreate an active thread for it.
          if (archivedAliasSet.has(alias) && status === 'declined') {
            return;
          }

          const rawMsg =
            (row.last_invite_message && String(row.last_invite_message)) || '';

          const safeMsg = rawMsg.trim() || 'Pending Chat Request';

          // For declined, show a clear message in the list
          const lastMessagePreview =
            status === 'declined' ? 'Invitation declined' : safeMsg;

          const tsSource =
            row.last_invited_at ||
            row.first_invited_at ||
            row.created_at ||
            null;
          const ts = tsSource ? Date.parse(tsSource) : Date.now();

          // When did the invite get declined (if ever)?
          const declinedAtSource = row.declined_at || null;
          const declinedTs = declinedAtSource ? Date.parse(declinedAtSource) : null;

          const displayName =
            (row.invitee_display_name &&
              String(row.invitee_display_name).trim()) ||
            null;
          const avatarUrl = row.invitee_avatar_url || null;

          // Canonical decline note: single field from backend
          const declineNote =
            row.decline_note && String(row.decline_note).trim()
              ? String(row.decline_note).trim()
              : null;

          const nextThread = {
            alias,
            dmId,
            peerUid: inviteeId,                 // "other" user for the inviter
            displayName,                        // invitee's name
            avatarUrl,                          // invitee's profile photo
            status,                             // 'pending' | 'declined' | ...
            lastInviteMessage: safeMsg,
            lastMessagePreview,
            lastMessageAt: Number.isFinite(ts) ? ts : Date.now(),
            // NEW: decline timestamp (ms since epoch) for cooldown logic
            declinedAt: Number.isFinite(declinedTs) ? declinedTs : null,

            // expose decline note directly on the thread for ChatView
            decline_note: declineNote,
            isOptimistic: true,
            direction: 'outgoing',
            inviteSent: status !== 'declined',
          };

          const idx = updated.findIndex(
            (t) => String(t.alias) === String(alias)
          );

          if (idx >= 0) {
            // Force immutable replacement to trigger React + persist update
            updated[idx] = {
              ...updated[idx],
              ...nextThread,
              dmId: nextThread.dmId,   // ensure overwrite
              status: nextThread.status,
              lastMessagePreview: nextThread.lastMessagePreview,
              // IMPORTANT: match nextThread semantics
              inviteSent: nextThread.inviteSent,
              direction: 'outgoing'
            };
          } else {
            additions.push(nextThread);
          }
        });

        // Build the next list of optimistic threads.
        // Even if there are no *new* threads to add, existing ones may have changed
        // (status, dmId, lastMessagePreview), so we must still commit `updated`.
        const next = additions.length ? [...additions, ...updated] : updated;
        return {
          optimisticThreads: next,
          completedInvites,
        };
      });
    } catch (e) {
      console.error('[messagingStore] ingestSentInvites failed', e);
    }
  },



  // Ingest pending invites (from /api/dm/pending) as incoming invite threads for the invitee
  ingestPendingInvites(invites) {
    try {
      const arr = Array.isArray(invites) ? invites : [];
      if (!arr.length) return;

      set((s) => {
        const existingOptimistic = s.optimisticThreads || [];
        const existingAliases = new Set(
          existingOptimistic.map((t) => String(t.alias || ''))
        );

        const existingArchived = s.archivedThreads || [];
        const archivedAliases = new Set(
          existingArchived.map((t) => String(t.alias || ''))
        );

        const mapped = [];
        const mappedAliasSet = new Set();
        let aliasToFocus = null;

        arr.forEach((row) => {
          if (!row) return;

          const dmId = row.dm_id;
          const inviterId = row.u_id_inviter;
          const inviteeId = row.u_id_invitee;

          if (!inviterId || !inviteeId) return;

          // Canonical alias for this pair, same pattern as inviter side
          const a = Math.min(inviterId, inviteeId);
          const b = Math.max(inviterId, inviteeId);
          const alias = `#eh.dm.u${a}_u${b}:ehandshake.com`;
          if (!alias) return;

          const aliasStr = String(alias);

          // If already active in optimisticThreads, check if direction changed
          // (e.g. user was inviter, got blocked, other user unblocked and re-invited them)
          if (existingAliases.has(aliasStr)) {
            const existingIdx = existingOptimistic.findIndex(
              (t) => String(t.alias) === aliasStr
            );
            if (existingIdx >= 0 && existingOptimistic[existingIdx].direction !== 'incoming') {
              // Direction changed — remove the old outgoing thread so we can replace it
              existingOptimistic.splice(existingIdx, 1);
              existingAliases.delete(aliasStr);
            } else {
              return;
            }
          }

          const rawMsg =
            (row.last_invite_message && String(row.last_invite_message)) || '';
          const safeMsg = rawMsg.trim() || 'You have a new invite';

          const tsSource =
            row.last_invited_at ||
            row.first_invited_at ||
            row.created_at ||
            null;
          const ts = tsSource ? Date.parse(tsSource) : Date.now();

          // Use inviter identity from the backend join
          const displayName =
            (row.inviter_display_name &&
              String(row.inviter_display_name).trim()) ||
            null;
          const avatarUrl = row.inviter_avatar_url || null;

          const thread = {
            alias,
            dmId,
            peerUid: inviterId, // "other" user for the invitee
            displayName,        // inviter's name (e.g., "Brett Toney")
            avatarUrl,          // inviter's profile photo URL
            lastInviteMessage: safeMsg,
            lastMessagePreview: safeMsg,
            lastMessageAt: Number.isFinite(ts) ? ts : Date.now(),
            isOptimistic: true,
            direction: 'incoming',
          };

          mapped.push(thread);
          mappedAliasSet.add(aliasStr);

          // If this alias was previously archived, we are being re-invited.
          // Pull it out of archive, put it at the top, and focus it.
          if (archivedAliases.has(aliasStr)) {
            aliasToFocus = aliasStr;
          }
        });

        if (!mapped.length && !aliasToFocus) {
          return {};
        }

        // Remove any archived copies of the aliases we just mapped
        const nextArchived = existingArchived.filter(
          (t) => !mappedAliasSet.has(String(t.alias || ''))
        );

        // New incoming invite threads appear on top of any existing optimistic threads
        const nextOptimistic = [...mapped, ...existingOptimistic];

        const nextState = {
          optimisticThreads: nextOptimistic,
          archivedThreads: nextArchived,
        };

        // If this was a re-invite for an archived conversation,
        // automatically focus that thread in ChatView / ChatList.
        if (aliasToFocus) {
          nextState.selectedThread = aliasToFocus;
        }

        return nextState;
      });
    } catch (e) {
      console.error('[messagingStore] ingestPendingInvites failed', e);
    }
  },


  // Load from localStorage (call once on app start or when opening the modal)
  loadPendingFromStorage() {
    try {
      const raw = localStorage.getItem('eh_pending_threads');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        set(() => ({ pendingThreads: parsed }));
      }
    } catch {}
  },



  // NOTE: optimisticThreads now rely solely on Zustand persist
}),
  {
    name: 'eh_messaging_v1',
    // Default (localStorage) is synchronous and compatible with persist
    partialize: (state) => ({
      // selectedThread is intentionally NOT persisted — always start fresh on Discover People
      pendingThreads: state.pendingThreads,
      optimisticThreads: state.optimisticThreads,
      archivedThreads: state.archivedThreads,
      candidateStarter: state.candidateStarter,
      completedInvites: state.completedInvites,
    }),
  }
));




